import React, { useMemo, useState } from 'react';
import {
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Plus,
  Minus,
  RefreshCw,
} from 'lucide-react';
import { ImportRow, ImportChangeType } from './types';

type FilterTab = 'all' | 'changed' | 'new' | 'unchanged' | 'invalid';

type ImportPreviewTableProps = {
  rows: ImportRow[];
};

const CHANGE_CONFIG: Record<ImportChangeType, {
  label: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  icon: React.ReactNode;
}> = {
  changed: {
    label: 'Alterado',
    bgClass: 'bg-amber-50',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-200',
    icon: <RefreshCw className="w-3 h-3" />,
  },
  new: {
    label: 'Novo',
    bgClass: 'bg-blue-50',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-200',
    icon: <Plus className="w-3 h-3" />,
  },
  unchanged: {
    label: 'Sem alteração',
    bgClass: 'bg-gray-50',
    textClass: 'text-gray-500',
    borderClass: 'border-gray-200',
    icon: <Minus className="w-3 h-3" />,
  },
};

function ChangeTypeBadge({ type }: { type: ImportChangeType }) {
  const config = CHANGE_CONFIG[type];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${config.bgClass} ${config.textClass} ${config.borderClass}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function ProfileTransition({ currentProfile, newProfile }: { currentProfile: string; newProfile: string }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 border border-gray-200 font-medium text-[11px]">
        {currentProfile || '(vazio)'}
      </span>
      <ArrowRight className="w-3 h-3 text-amber-500 flex-shrink-0" />
      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 font-medium text-[11px]">
        {newProfile}
      </span>
    </div>
  );
}

export const ImportPreviewTable: React.FC<ImportPreviewTableProps> = ({ rows }) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');

  const counts = useMemo(() => {
    const valid = rows.filter((r) => r.valid);
    const invalid = rows.filter((r) => !r.valid);
    return {
      all: rows.length,
      changed: valid.filter((r) => r.changeType === 'changed').length,
      new: valid.filter((r) => r.changeType === 'new').length,
      unchanged: valid.filter((r) => r.changeType === 'unchanged').length,
      invalid: invalid.length,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    switch (activeFilter) {
      case 'changed':
        return rows.filter((r) => r.valid && r.changeType === 'changed');
      case 'new':
        return rows.filter((r) => r.valid && r.changeType === 'new');
      case 'unchanged':
        return rows.filter((r) => r.valid && r.changeType === 'unchanged');
      case 'invalid':
        return rows.filter((r) => !r.valid);
      default:
        return rows;
    }
  }, [rows, activeFilter]);

  const tabs: { key: FilterTab; label: string; count: number; color: string }[] = [
    { key: 'all', label: 'Todos', count: counts.all, color: 'text-gray-600' },
    { key: 'changed', label: 'Alterados', count: counts.changed, color: 'text-amber-600' },
    { key: 'new', label: 'Novos', count: counts.new, color: 'text-blue-600' },
    { key: 'unchanged', label: 'Sem alteração', count: counts.unchanged, color: 'text-gray-500' },
    { key: 'invalid', label: 'Inválidos', count: counts.invalid, color: 'text-red-600' },
  ];

  const getRowBg = (row: ImportRow) => {
    if (!row.valid) return 'bg-red-50/50';
    if (row.changeType === 'changed') return 'bg-amber-50/40';
    if (row.changeType === 'new') return 'bg-blue-50/30';
    return 'bg-white';
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrar itens da importação">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeFilter === tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              activeFilter === tab.key
                ? 'bg-gray-900 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            <span
              className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                activeFilter === tab.key
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden max-h-[280px] overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                Group ID
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                Group Name
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                Perfil
              </th>
              <th className="px-3 py-2.5 text-left font-semibold text-gray-500 uppercase tracking-wider">
                Info
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-gray-400 text-sm">
                  Nenhum item nesta categoria
                </td>
              </tr>
            )}
            {filteredRows.map((row, idx) => (
              <tr key={idx} className={getRowBg(row)}>
                <td className="px-3 py-2.5">
                  {!row.valid ? (
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  ) : row.changeType ? (
                    <ChangeTypeBadge type={row.changeType} />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  )}
                </td>
                <td className="px-3 py-2.5 font-mono text-gray-700">
                  {row.groupId || '-'}
                </td>
                <td className="px-3 py-2.5 text-gray-700">
                  {row.groupName || '-'}
                </td>
                <td className="px-3 py-2.5">
                  {!row.valid ? (
                    <span className="text-gray-400">{row.profile || '-'}</span>
                  ) : row.changeType === 'changed' ? (
                    <ProfileTransition
                      currentProfile={row.currentProfile || ''}
                      newProfile={row.profile}
                    />
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-100 font-medium">
                      {row.profile}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {!row.valid ? (
                    <span className="text-red-600">{row.reason}</span>
                  ) : row.changeType === 'new' ? (
                    <span className="text-blue-600 text-[11px]">Grupo não cadastrado</span>
                  ) : row.changeType === 'unchanged' ? (
                    <span className="text-gray-400 text-[11px]">Perfil já é o mesmo</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
